import React, { useState, useEffect } from "react";
import Navbar from "./Navbar";
import { supabase } from "./supabase";

const categories = [
  "Jobs & Opportunity",
  "Infrastructure",
  "Higher Education",
  "Housing",
  "Agriculture",
  "Arts & Culture",
  "Water & Sanitation",
  "Science & Innovation",
];

const provinces = ["Gauteng", "Western Cape", "KwaZulu-Natal", "Eastern Cape", "Limpopo", "Mpumalanga", "North West", "Free State", "Northern Cape"];

const inputStyle = {
  width: "100%",
  background: "#F5F0E8",
  border: "1.5px solid #EDE8DF",
  borderRadius: 8,
  padding: "11px 14px",
  color: "#1A1A2E",
  fontSize: 16,
  fontFamily: "var(--font-body)",
  outline: "none",
  marginBottom: 16,
  boxSizing: "border-box",
};

const labelStyle = {
  fontSize: 11,
  color: "#888",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  marginBottom: 6,
  display: "block",
};

export default function Community() {
  const [user, setUser] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [myVotes, setMyVotes] = useState([]);
  const [sort, setSort] = useState("Most voted");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({ title: "", category: "", province: "", description: "" });

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user);
      if (user) fetchMyVotes(user.id);
    });
    fetchSuggestions();
  }, []);

  const fetchSuggestions = async () => {
    const { data } = await supabase.from("suggestions").select("*").order("votes", { ascending: false });
    if (data) setSuggestions(data);
  };

  const fetchMyVotes = async (userId) => {
    const { data } = await supabase.from("suggestion_votes").select("suggestion_id").eq("user_id", userId);
    if (data) setMyVotes(data.map(v => v.suggestion_id));
  };

  const handleSubmit = async () => {
    if (!form.title || !form.category || !form.province || !form.description) {
      setError("Please fill in all fields.");
      return;
    }
    if (form.description.length < 20) {
      setError("Please write at least 20 characters describing your idea.");
      return;
    }
    setLoading(true);
    setError("");
    const { error: submitError } = await supabase.from("suggestions").insert({
      user_id: user.id,
      title: form.title,
      category: form.category,
      province: form.province,
      description: form.description,
      votes: 0,
    });
    setLoading(false);
    if (submitError) {
      setError(submitError.message);
    } else {
      setSuccess(true);
      setShowForm(false);
      setForm({ title: "", category: "", province: "", description: "" });
      fetchSuggestions();
    }
  };

  const handleVote = async (suggestionId) => {
    if (!user) {
      window.location.href = "/login";
      return;
    }
    const hasVoted = myVotes.includes(suggestionId);
    if (hasVoted) {
      await supabase.from("suggestion_votes").delete().eq("suggestion_id", suggestionId).eq("user_id", user.id);
      const suggestion = suggestions.find(s => s.id === suggestionId);
      await supabase.from("suggestions").update({ votes: Math.max(0, suggestion.votes - 1) }).eq("id", suggestionId);
      setMyVotes(myVotes.filter(id => id !== suggestionId));
    } else {
      await supabase.from("suggestion_votes").insert({ suggestion_id: suggestionId, user_id: user.id });
      const suggestion = suggestions.find(s => s.id === suggestionId);
      await supabase.from("suggestions").update({ votes: suggestion.votes + 1 }).eq("id", suggestionId);
      setMyVotes([...myVotes, suggestionId]);
    }
    fetchSuggestions();
  };

  const sorted = [...suggestions].sort((a, b) => {
    if (sort === "Newest") return new Date(b.created_at) - new Date(a.created_at);
    return b.votes - a.votes;
  });

  return (
    <div style={{ fontFamily: "var(--font-body)", background: "#F5F0E8", minHeight: "100vh", color: "#1A1A2E" }}>

      <style>{`
        .comm-header { padding: 56px 48px 48px; }
        .comm-success { max-width: 700px; margin: 32px auto 0; padding: 16px 24px; }
        .comm-submit { max-width: 1100px; margin: 0 auto; padding: 32px 48px 0; }
        .comm-form { background: #fff; border: 1px solid #EDE8DF; border-radius: 20px; padding: 32px; }
        .comm-main { max-width: 1100px; margin: 0 auto; padding: 24px 48px 72px; }
        .comm-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 16px; }
        .comm-submitbar { background: #fff; border: 1px solid #EDE8DF; border-radius: 16px; padding: 20px 24px; display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
        @media (max-width: 768px) {
          .comm-header { padding: 36px 20px 32px; }
          .comm-success { margin: 20px 20px 0; }
          .comm-submit { padding: 20px 20px 0; }
          .comm-form { padding: 20px; }
          .comm-main { padding: 20px 20px 48px; }
          .comm-grid { grid-template-columns: 1fr; gap: 12px; }
          .comm-submitbar { flex-direction: column; align-items: flex-start; }
          .comm-submitbar button { width: 100%; text-align: center; }
        }
      `}</style>

      <Navbar />

      {/* HEADER */}
      <div className="comm-header" style={{ background: "#fff", borderBottom: "1px solid #EDE8DF", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "rgba(27,58,107,0.07)", border: "1px solid rgba(27,58,107,0.15)", borderRadius: 99, padding: "5px 16px", fontSize: 11, color: "#1B3A6B", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 20 }}>
          Community
        </div>
        <h1 style={{ fontSize: "clamp(26px, 4vw, 44px)", fontWeight: 900, color: "#1A1A2E", letterSpacing: "-0.02em", margin: "0 0 16px", lineHeight: 1.1 }}>
          What should <span style={{ color: "#1B3A6B" }}>Forge SA</span> build next?
        </h1>
        <p style={{ fontSize: 15, color: "#888", lineHeight: 1.8, maxWidth: 540, margin: "0 auto" }}>
          Your voice matters before a project even begins. Suggest an idea, vote on what others have proposed, and help shape what gets built.
        </p>
      </div>

      {/* SUCCESS */}
      {success && (
        <div className="comm-success" style={{ background: "rgba(46,125,50,0.07)", border: "1px solid rgba(46,125,50,0.2)", borderRadius: 12, textAlign: "center", color: "#2E7D32", fontWeight: 700 }}>
          Thank you! Your idea has been added for the community to vote on.
        </div>
      )}

      {/* SUBMIT BAR */}
      <div className="comm-submit">
        {!showForm ? (
          <div className="comm-submitbar">
            <div style={{ fontSize: 13, color: "#4A4A6A" }}>
              Have an idea for your community? <strong style={{ color: "#1A1A2E" }}>Share it and let others vote.</strong>
            </div>
            <button
              onClick={() => user ? setShowForm(true) : window.location.href = "/login"}
              style={{ background: "#1B3A6B", color: "#fff", border: "none", borderRadius: 99, padding: "10px 22px", fontSize: 12, fontWeight: 700, fontFamily: "var(--font-body)", cursor: "pointer", whiteSpace: "nowrap" }}
            >
              + Suggest an idea
            </button>
          </div>
        ) : (
          <div className="comm-form">
            <div style={{ fontSize: 18, fontWeight: 800, color: "#1A1A2E", marginBottom: 24 }}>Share Your Idea</div>

            <label style={labelStyle}>Title</label>
            <input style={inputStyle} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Borehole for our village" />

            <label style={labelStyle}>Category</label>
            <select style={inputStyle} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              <option value="">Select category</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>

            <label style={labelStyle}>Province</label>
            <select style={inputStyle} value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })}>
              <option value="">Select province</option>
              {provinces.map(p => <option key={p} value={p}>{p}</option>)}
            </select>

            <label style={labelStyle}>Describe your idea</label>
            <textarea
              style={{ ...inputStyle, resize: "vertical" }}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What does your community need, and why would this make a real difference?"
              rows={4}
            />
            <div style={{ fontSize: 11, color: "#888", marginTop: -12, marginBottom: 16 }}>{form.description.length} characters</div>

            {error && (
              <div style={{ background: "rgba(192,57,43,0.07)", border: "1px solid rgba(192,57,43,0.2)", borderRadius: 8, padding: "12px 16px", marginBottom: 16, fontSize: 13, color: "#C0392B" }}>
                {error}
              </div>
            )}

            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => setShowForm(false)}
                style={{ flex: 1, background: "transparent", border: "1.5px solid #EDE8DF", borderRadius: 99, padding: "13px", color: "#888", fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "var(--font-body)" }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                style={{ flex: 2, background: loading ? "#EDE8DF" : "#1B3A6B", border: "none", borderRadius: 99, padding: "13px", color: loading ? "#888" : "#fff", fontWeight: 800, fontSize: 14, cursor: loading ? "not-allowed" : "pointer", fontFamily: "var(--font-body)" }}
              >
                {loading ? "Submitting..." : "Submit Idea"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* MAIN */}
      <div className="comm-main">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
          <div style={{ fontSize: 13, color: "#888" }}>
            Showing <strong style={{ color: "#1B3A6B" }}>{sorted.length}</strong> suggestion{sorted.length !== 1 ? "s" : ""}
          </div>
          <select
            style={{ padding: "7px 14px", border: "1px solid #EDE8DF", borderRadius: 8, fontSize: 12, background: "#fff", color: "#1A1A2E", outline: "none", fontFamily: "var(--font-body)", cursor: "pointer" }}
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            <option>Most voted</option>
            <option>Newest</option>
          </select>
        </div>

        {sorted.length === 0 ? (
          <div style={{ textAlign: "center", padding: "64px 0", color: "#888", fontSize: 14 }}>
            No suggestions yet. Be the first to share an idea for your community.
          </div>
        ) : (
          <div className="comm-grid">
            {sorted.map((s) => {
              const voted = myVotes.includes(s.id);
              return (
                <div key={s.id} style={{ background: "#fff", border: "1px solid #EDE8DF", borderRadius: 16, padding: 20, display: "flex", flexDirection: "column", gap: 10 }}>
                  <span style={{ display: "inline-block", background: "#F5F0E8", color: "#1B3A6B", fontSize: 10, padding: "3px 10px", borderRadius: 99, fontWeight: 700, letterSpacing: "0.04em", alignSelf: "flex-start" }}>
                    {s.category}
                  </span>
                  <div style={{ fontSize: 14, fontWeight: 800, color: "#1A1A2E" }}>{s.title}</div>
                  <div style={{ fontSize: 12, color: "#888", lineHeight: 1.65, flex: 1 }}>{s.description}</div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #EDE8DF", paddingTop: 10 }}>
                    <span style={{ fontSize: 11, color: "#888" }}>{s.province}</span>
                    <button
                      onClick={() => handleVote(s.id)}
                      style={{ display: "flex", alignItems: "center", gap: 6, background: voted ? "#1B3A6B" : "#F5F0E8", color: voted ? "#fff" : "#1B3A6B", border: `1px solid ${voted ? "#1B3A6B" : "#EDE8DF"}`, borderRadius: 99, padding: "6px 14px", fontSize: 12, fontWeight: 700, fontFamily: "var(--font-body)", cursor: "pointer" }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill={voted ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="18 15 12 9 6 15"/>
                      </svg>
                      {s.votes}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

    </div>
  );
}